import { CAMPAIGN_STATUSES, statusOrder, maxStatus } from "../constants/status";
import type { CampaignStatus } from "../constants/status";

/**
 * Status Preview Helper
 *
 * IMPORTANT: This is for UI previews and tests ONLY.
 * The actual status is calculated by the database trigger.
 *
 * Use this when:
 * - Showing a preview of what status WOULD BE before saving
 * - Writing tests that don't hit the database
 *
 * Do NOT use this for:
 * - Getting the current status (use campaign.campaign_status from API)
 * - Updating status (done automatically by DB)
 */
export function calculateCampaignStatusPreview(input: {
  requiredFieldsReady: boolean;
  deliverableStatuses: string[];
  hasIssuedInvoices: boolean;
  allCompanyInvoicesPaidOrCancelled: boolean;
  allInfluencerInvoicesPaid: boolean;
}): CampaignStatus {
  const allPublished =
    input.deliverableStatuses.length > 0 &&
    input.deliverableStatuses.every(s => s === "Published");

  // Check if there are any deliverables in Production or later stages (not Draft)
  const hasNonDraftDeliverables = input.deliverableStatuses.some(
    s => s !== "Draft" && s !== ""
  );

  if (allPublished && input.allCompanyInvoicesPaidOrCancelled && input.allInfluencerInvoicesPaid) {
    return "Completed";
  }

  if (allPublished && input.hasIssuedInvoices) {
    return "Billing";
  }

  // Campaign is "In Progress" if required fields are ready AND there's at least one non-Draft deliverable
  if (input.requiredFieldsReady && hasNonDraftDeliverables) {
    return "In Progress";
  }

  // Default: Consultation (no deliverables, or all deliverables are Draft)
  return "Consultation";
}

export { CAMPAIGN_STATUSES, statusOrder, maxStatus };
export type { CampaignStatus };
